import {
  DC_Repositories_Base_Enumerations_BusinessUnit,
  DC_Repositories_Base_Enumerations_Label,
  RequestModels_SmartHeatPump_UpdateOptimizationDeviceSettingsRequestModel,
  ResponseModels_SmartHeatPump_DeviceTelemetryResponseModel,
  ResponseModels_SmartHeatPump_SmartHeatPumpUsageDetailsResponseModel,
  ResponseModels_SmartHeatPump_SmartHeatPumpUsageOverviewResponseModel,
  SmartHeatPump_TemperatureControlModeRequestModel,
  SmartHeatPump_TemperatureSettingRequestModel,
} from '@monorepo-types/dc';

import { request } from '../client';
import type { ApiRequestConfig } from '../client/types';

type GetSmartHeatPumpUsageOverview = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  deviceId: string;
  startDate: string;
  endDate: string;
};
/**
 * Method to get usage overview for a specific customer with deviceId
 *
 * @returns ResponseModels_SmartHeatPump_SmartHeatPumpUsageOverviewResponseModel Success
 */
export function getSmartHeatPumpUsageOverview(
  { businessUnit, label, customerId, deviceId, startDate, endDate }: GetSmartHeatPumpUsageOverview,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_SmartHeatPump_SmartHeatPumpUsageOverviewResponseModel> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/smartheatpump/${deviceId}/usages/overview`,
      query: { startDate, endDate },
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type GetSmartHeatPumpUsageDetails = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  deviceId: string;
};
/**
 * Method to get usage details for a specific customer with deviceId
 *
 * @returns ResponseModels_SmartHeatPump_SmartHeatPumpUsageDetailsResponseModel Success
 */
export function getSmartHeatPumpUsageDetails(
  { businessUnit, label, customerId, deviceId }: GetSmartHeatPumpUsageDetails,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_SmartHeatPump_SmartHeatPumpUsageDetailsResponseModel> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/smartheatpump/${deviceId}/usages/details`,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type GetDeviceTelemetry = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  deviceId: string;
};
/**
 * Method to get device telemetry data
 *
 * @returns ResponseModels_SmartHeatPump_DeviceTelemetryResponseModel Success
 */
export function getDeviceTelemetry(
  { businessUnit, label, customerId, deviceId }: GetDeviceTelemetry,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_SmartHeatPump_DeviceTelemetryResponseModel> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/smartheatpump/${deviceId}/telemetry`,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type UpdateDeviceRoomTemperature = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  deviceId: string;
  requestBody: SmartHeatPump_TemperatureSettingRequestModel;
};
/**
 * Method to update the temperature settings for a specific device
 *
 * @returns any Success
 */
export function updateDeviceRoomTemperature(
  { businessUnit, label, customerId, deviceId, requestBody }: UpdateDeviceRoomTemperature,
  requestConfig: ApiRequestConfig = {},
): Promise<any> {
  return request(
    {
      method: 'PATCH',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/smartheatpump/${deviceId}/temperature`,
      body: requestBody,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type UpdateDeviceControlMode = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  deviceId: string;
  requestBody: SmartHeatPump_TemperatureControlModeRequestModel;
};
/**
 * Method to update the mode of a specific device
 *
 * @returns any Success
 */
export function updateDeviceControlMode(
  { businessUnit, label, customerId, deviceId, requestBody }: UpdateDeviceControlMode,
  requestConfig: ApiRequestConfig = {},
): Promise<any> {
  return request(
    {
      method: 'PATCH',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/smartheatpump/${deviceId}/mode`,
      body: requestBody,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type UpdateOptimizationSettings = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  deviceId: string;
  requestBody?: RequestModels_SmartHeatPump_UpdateOptimizationDeviceSettingsRequestModel;
};
/**
 * Update Optimization settings for a specific device
 *
 * @returns any Success
 */
export function updateOptimizationSettings(
  { businessUnit, label, customerId, deviceId, requestBody }: UpdateOptimizationSettings,
  requestConfig: ApiRequestConfig = {},
): Promise<any> {
  return request(
    {
      method: 'PATCH',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/smartheatpump/${deviceId}/settings/optimization`,
      body: requestBody,
      errors: { 400: 'Bad Request', 500: 'Server Error' },
    },
    requestConfig,
  );
}
